import SuperFetch from 'library/helpers/superFetch';
import { call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { permissionsActions } from 'reduxStore/permissions/slice';
import { Notification } from 'zComponents/atoms';
import { teamsActions } from './slice';

export function* getTeams() {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const teams = yield call(() => SuperFetch.get('/teams'));

		// Get teams from the current organisation.
		if (teams.statusCode === 200) {
			yield put(teamsActions.loadTeamsSuccess({ orgID, data: teams.data }));
		} else {
			throw Error(teams.error.message || teams.error || 'Failed to load teams.');
		}
	} catch (error) {
		yield put(teamsActions.loadTeamsError(error));
	}
}

export function* getTeamsSuccess() {
	yield put(teamsActions.loadAllTeamsMembers());
}

export function* getAllTeamsMembers() {
	try {
		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile);
		const teams = yield call(() => SuperFetch.get('/teams/getAllTeamsAllMembers'));

		// Get teams from the current organisation.
		if (teams.statusCode === 200) {
			yield put(teamsActions.loadAllTeamsMembersSuccess({ orgID, data: teams.data }));
		} else {
			throw Error(teams.error.message || teams.error || 'Failed to load teams.');
		}
	} catch (error) {
		yield put(teamsActions.loadAllTeamsMembersError(error));
	}
}

export function* addTeam(action) {
	try {
		// Get the data from the action payload
		const { personIDs, ...values } = action.payload.data;
		//	Get the orgID from the selected profile
		const team = yield call(() => SuperFetch.post(`/teams`, values));

		if (team.statusCode === 201 || team.statusCode === 200) {
			const teamID = team.data?.teamID;

			// If there are personIDs, add members to the team
			if (personIDs && personIDs.length > 0 && teamID) {
				yield put(teamsActions.addTeamMember({ teamID, personIDs }));
			}
			yield put(teamsActions.addTeamSuccess());
			Notification('success', 'Team has been added successfully.');
		} else {
			throw Error(team.error.message || team.error || 'Failed to add team.');
		}
	} catch (error) {
		yield put(teamsActions.addTeamError(error));
		Notification('error', error.message);
	}
}

// 🔥Actions here after add team
export function* addTeamSuccess() {
	yield put(teamsActions.loadTeams());
}

export function* updateTeam(action) {
	try {
		// Get the teamID and data from the action payload
		const { teamID, data } = action.payload;

		// Update the team
		const updateTeam = yield call(() => SuperFetch.patch(`/teams/${teamID}`, data));

		if (updateTeam.statusCode === 200) {
			yield put(teamsActions.updateTeamSuccess());
			Notification('success', 'Team has been updated successfully.');
		} else {
			throw Error(updateTeam.error.message || updateTeam.error || 'Failed to update team.');
		}
	} catch (error) {
		yield put(teamsActions.updateTeamError(error));
		Notification('error', error.message);
	}
}

// 🔥Actions here after update team
export function* updateTeamSuccess() {
	yield put(teamsActions.loadTeams());
}

export function* deleteTeam(action) {
	try {
		// Get the teamID from the action
		const { teamID } = action.payload;

		// Delete the team
		const deleteTeam = yield call(() => SuperFetch.delete(`/teams/${teamID}`));

		if (deleteTeam.statusCode === 200) {
			yield put(teamsActions.deleteTeamSuccess());
		} else {
			throw Error(deleteTeam.error.message || deleteTeam.error || 'Failed to delete team.');
		}
	} catch (error) {
		yield put(teamsActions.deleteTeamError(error));
		Notification('error', error.message);
	}
}

// 🔥Actions here after delete team
export function* deleteTeamSuccess() {
	yield put(teamsActions.loadTeams());
	yield put(permissionsActions.loadPermissions());
}

export function* getTeamMembers(action) {
	try {
		// Get the teamID from the action payload
		const { teamID } = action.payload;

		// Get the orgID from the selected profile
		const { orgID } = yield select((state) => state.Auth.selectedProfile); // Get orgID

		// Get the members of the team
		const members = yield call(() => SuperFetch.get(`/teams/${teamID}/members`));

		if (members.statusCode === 200) {
			yield put(teamsActions.loadTeamMembersSuccess({ orgID, teamID, data: members.data }));
		} else {
			throw Error(members.error.message || members.error || 'Failed to load team members.');
		}
	} catch (error) {
		yield put(teamsActions.loadTeamMembersError(error));
	}
}

export function* addTeamMember(action) {
	try {
		// Get the teamID and personID from the action payload
		const { teamID, personIDs } = action.payload;

		// Add the member to the team
		const members = yield call(() => SuperFetch.post(`/teams/${teamID}/members`, { personIDs }));

		if (members.statusCode === 200 || members.statusCode === 201) {
			yield put(teamsActions.addTeamMemberSuccess({ teamID }));
			Notification('success', 'Team(s) member(s) added successfully.');
		} else {
			throw Error(members.error.message || members.error || 'Failed to add team member.');
		}
	} catch (error) {
		yield put(teamsActions.addTeamMemberError(error));
		Notification('error', error.message);
	}
}

// 🔥Actions here after add member into the team
export function* addTeamMemberSuccess(action) {
	const { teamID } = action.payload;
	yield put(teamsActions.loadTeamMembers({ teamID }));
	yield put(permissionsActions.loadPermissions());

	// Delay for 1 second for reset complete add members team
	// to show the success notification
	// before resetting the state
	// to avoid flickering of the notification
	yield delay(1000);
	yield put(teamsActions.resetCompleteAddMembersTeam());
}

export function* deleteTeamMember(action) {
	try {
		// Get the teamID and memberID from the action payload
		const { teamID, personIDs } = action.payload;

		// API request to delete the member from the team
		const members = yield call(() => SuperFetch.delete(`/teams/${teamID}/members`, { personIDs }));

		if (members.statusCode === 200) {
			yield put(teamsActions.deleteTeamMemberSuccess({ teamID }));
			Notification('success', 'Team(s) member(s) deleted successfully.');
		} else {
			throw Error(members.error.message || members.error || 'Failed to delete team member.');
		}
	} catch (error) {
		yield put(teamsActions.deleteTeamMemberError(error));
		Notification('error', error.message);
	}
}

// 🔥Actions here after delete member from the team
export function* deleteTeamMemberSuccess(action) {
	const { teamID } = action.payload;

	yield put(teamsActions.loadTeamMembers({ teamID }));
	yield put(permissionsActions.loadPermissions());
}

// Root saga
export default function* teamsSaga() {
	yield takeLatest(teamsActions.loadTeams, getTeams);
	yield takeLatest(teamsActions.loadTeamsSuccess, getTeamsSuccess);
	yield takeLatest(teamsActions.loadAllTeamsMembers, getAllTeamsMembers);
	yield takeLatest(teamsActions.addTeam, addTeam);
	yield takeLatest(teamsActions.addTeamSuccess, addTeamSuccess);
	yield takeLatest(teamsActions.updateTeam, updateTeam);
	yield takeLatest(teamsActions.updateTeamSuccess, updateTeamSuccess);
	yield takeLatest(teamsActions.deleteTeam, deleteTeam);
	yield takeLatest(teamsActions.deleteTeamSuccess, deleteTeamSuccess);
	yield takeEvery(teamsActions.loadTeamMembers, getTeamMembers);
	yield takeEvery(teamsActions.addTeamMember, addTeamMember);
	yield takeEvery(teamsActions.addTeamMemberSuccess, addTeamMemberSuccess);
	yield takeEvery(teamsActions.deleteTeamMember, deleteTeamMember);
	yield takeEvery(teamsActions.deleteTeamMemberSuccess, deleteTeamMemberSuccess);
}
